import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Describes the widget's <script> tag: its src plus any data-* attributes the
 * vendor's embed snippet requires (e.g. data-app-id, data-widget-key).
 */
export interface ChatWidgetConfig {
  src: string;
  attributes?: Record<string, string>;
}

/**
 * Gates the Bolofy support-chat widget so that, if it is ever re-enabled, it can
 * never load on a payment/card-entry route. See the Payment Script Registry
 * (PCI-643-SCR-2026-001), SCR-02, for the justification and authorization this
 * implements.
 *
 * This service is not invoked from anywhere today - the widget stays fully off,
 * exactly as it is now with the <script> tag commented out of index.html.
 * Re-enabling it requires two separate things: (1) a written business
 * justification recorded in the registry, and (2) a single explicit call to
 * init() with the real widget config, added from wherever that PR chooses (e.g.
 * AppComponent). Until both happen, this file has no effect on the app.
 */
export interface ChatWidgetConfigs {
  preAuth: ChatWidgetConfig;   // logged-out pages (login)
  postAuth: ChatWidgetConfig;  // logged-in app, location-selection
}

type WidgetMode = 'preAuth' | 'postAuth';

@Injectable({ providedIn: 'root' })
export class ChatWidgetGateService {
  private static readonly SCRIPT_ID = 'bolofy-chat-widget';
  private static readonly HIDE_STYLE_ID = 'folio-hide-style';
  private static readonly WIDGET_ROOT_ID = 'folio-root';

  // Routes where the chatbot must NOT be shown.
  // 'login' and 'location-selection' are intentionally not in this list.
  // Matches the path itself or anything below it (e.g. '/form' matches '/form/abc').
  private static readonly HIDDEN_ROUTES = [
    '/pay-now',
    '/form',
    '/policy-form',
    '/refund-settlement',
    '/error',
    '/other',
    '/verify-invite-member',
    '/set-your-password',
    '/sign-up',
    '/registration',
    // '/forgot-password',
    '/logout',
    '/contact-us',
  ];

  private loadedMode: WidgetMode | null = null;

  // Route path segments that host card-entry or other payment-adjacent flows,
  // taken from the registry's SCR-01 "Reached from" list. Review and extend this
  // whenever a new payment-related route is added - see registry remediation
  // item 5 (re-review trigger).
  private static readonly PAYMENT_ROUTE_SEGMENTS = [
    'payments',
    'payment-plan',
    'adjust-plan',
    'preview-payment-plan',
    'pre-payment',
    'card-on-file',
    'invoice-pay',
    'processor-setting',
    'fix-failed-payment',
    'agreement-cc',
    'take-payment',
  ];
  private initialized = false;
  private observer?: PerformanceObserver;
  private safetyTimer?: ReturnType<typeof setTimeout>;
  private revealTimer?: ReturnType<typeof setTimeout>;

  constructor(private router: Router) {}

  /**
   * Activates the widget with route-scoping. `config.src` and any
   * `config.attributes` (accessToken, app id, widget key, etc.) must come from
   * environment config - they are deliberately not hardcoded here. Also add the
   * widget's host (and its API host, under connect-src) to the CSP in
   * staticwebapp.config.json before calling this, or the browser will block the
   * script regardless.
   */
  init(configs: ChatWidgetConfigs): void {
    if (this.initialized) return; // guard against double init() = double subscription
    this.initialized = true;

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.syncWithRoute(event.urlAfterRedirects, configs));

    // On a fresh page load the first navigation has not finished yet, so the
    // first NavigationEnd above makes the decision with the real URL.
    if (this.router.navigated) {
      this.syncWithRoute(this.router.url, configs);
    }
  }

  private syncWithRoute(url: string, configs: ChatWidgetConfigs): void {
    if (this.isHiddenRoute(url)) {
      this.remove();
      return;
    }

    const mode: WidgetMode = this.isAuthenticated() ? 'postAuth' : 'preAuth';

    if (this.loadedMode === mode) return; // already showing the right widget

    this.remove();                         // swap: clear the old one first
    this.load(configs[mode], mode);
  }

  private isHiddenRoute(url: string): boolean {
    // strip query string / fragment, normalise case and trailing slash
    const path = url.split('?')[0].split('#')[0].toLowerCase().replace(/\/+$/, '');
    return ChatWidgetGateService.HIDDEN_ROUTES.some(
      (r) => path === r || path.startsWith(r + '/')
    );
  }
  
  private isPaymentRoute(url: string): boolean {
    const lower = url.toLowerCase();
    return ChatWidgetGateService.PAYMENT_ROUTE_SEGMENTS.some((segment) =>
      lower.includes(segment)
    );
  }

  private isAuthenticated(): boolean {
    return !!localStorage.getItem('authToken') ||
      !!localStorage.getItem('loggedInUser');
  }

  private load(config: ChatWidgetConfig, mode: WidgetMode): void {
    if (document.getElementById(ChatWidgetGateService.SCRIPT_ID)) return;

    // Hide until the widget's own bootstrap request has finished
    // (prevents the right-side flash before the admin position is applied).
    this.hideWidget();

    const script = document.createElement('script');
    script.id = ChatWidgetGateService.SCRIPT_ID;
    script.src = config.src;
    script.async = true;
    Object.entries(config.attributes ?? {}).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });

    document.head.appendChild(script);
    this.loadedMode = mode;
  }

  private hideWidget(): void {
    this.clearHideTimers();

    if (!document.getElementById(ChatWidgetGateService.HIDE_STYLE_ID)) {
      const style = document.createElement('style');
      style.id = ChatWidgetGateService.HIDE_STYLE_ID;
      style.textContent = `#${ChatWidgetGateService.WIDGET_ROOT_ID}{visibility:hidden !important;}`;
      document.head.appendChild(style);
    }

    const hiddenAt = performance.now();

    const reveal = () => {
      this.clearHideTimers();
      this.revealTimer = setTimeout(() => this.removeHideStyle(), 150);
    };

    this.observer = new PerformanceObserver((list) => {
      const done = list
        .getEntries()
        .some((e) => e.name.includes('/api/chat/bootstrap') && e.startTime >= hiddenAt);
      if (done) reveal();
    });
    this.observer.observe({ type: 'resource', buffered: true });

    // Safety net so the widget is never stuck hidden
    this.safetyTimer = setTimeout(reveal, 4000);
  }

  private clearHideTimers(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    if (this.safetyTimer) clearTimeout(this.safetyTimer);
    if (this.revealTimer) clearTimeout(this.revealTimer);
    this.safetyTimer = undefined;
    this.revealTimer = undefined;
  }

  private removeHideStyle(): void {
    document.getElementById(ChatWidgetGateService.HIDE_STYLE_ID)?.remove();
  }

  /** Public so logout() can call it directly. */
  remove(): void {
    this.clearHideTimers();
    this.removeHideStyle();
    document.getElementById(ChatWidgetGateService.SCRIPT_ID)?.remove();
    document.getElementById(ChatWidgetGateService.WIDGET_ROOT_ID)?.remove();
    this.loadedMode = null;
    // Removing the <script> tag does not tear down any global widget state
    // Bolofy's script may already have set up (e.g. a floating iframe/badge).
    // Before enabling this, confirm with Bolofy whether they expose a
    // destroy/unmount API and call it here too - do not assume tag removal
    // alone hides an already-rendered widget.
  }
}